import { useEffect } from 'react'

import { useNavigate } from 'react-router-dom'

import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { ANY_LANGUAGE_ID } from 'src/Constants/constants'
import { ArtistRolesEnum } from 'src/Constants/enums'
import { useSourceBusterEvent } from 'src/Hooks/useSourceBusterEvent'
import { artistRegistrationCompleteEvent } from 'src/Helpers/TagManager'
import { sendFirstCampaignCreatedEvents } from 'src/Helpers/TagManager/CampaignsEvents'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import {
  useCreatePreReleaseSpotifyCampaignMutation,
  useCreateSpotifyCampaignMutation,
  useUpdateSpotifyCampaignGenresMutation,
  useUpdateSpotifyCampaignLanguagesMutation,
} from 'src/Redux/spotify-campaignApi'
import { useCreateNewTiktokCampaignMutation } from 'src/Redux/createNewCampaignApi'
import { useCreateAdCampaignMutation } from 'src/Redux/ad-campaignApi'
import {
  clearPendingCampaignData,
  readPendingCampaignData,
} from 'src/Utils/pendingCampaignData'
import { createdFirst } from 'src/Router/routes'
import { buildPreReleaseCreatedUrl } from 'src/Pages/Artist/SuccessCreatedFirstPreRelease/helpers'

import { getTokenFromUrl, getUserFromUrl } from './helpers'

export const useApplyPendingCampaignCreation = (): null => {
  const navigate = useNavigate()
  const { search } = useCustomSearchParams()
  const [createSpotifyCampaign] = useCreateSpotifyCampaignMutation()
  const [updateSpotifyCampaignGenres] = useUpdateSpotifyCampaignGenresMutation()
  const [updateSpotifyCampaignLanguages] =
    useUpdateSpotifyCampaignLanguagesMutation()
  const [createNewTiktokCampaign] = useCreateNewTiktokCampaignMutation()
  const [createPreReleaseSpotifyCampaign] =
    useCreatePreReleaseSpotifyCampaignMutation()
  const [createAdCampaign] = useCreateAdCampaignMutation()
  const { sendCreateFirstCampaignSourceBusterEvent } = useSourceBusterEvent()

  const token = getTokenFromUrl(search)
  const user = getUserFromUrl(search.get('user'))

  useEffect(() => {
    if (!token) {
      return
    }

    const pending = readPendingCampaignData()

    if (!pending) {
      return
    }

    clearPendingCampaignData()

    const userId = user ? String(user.user.id) : void 0

    if (pending.platform === 'spotify') {
      void createSpotifyCampaign(pending.body)
        .unwrap()
        .then((campaign) => {
          void updateSpotifyCampaignGenres({
            id: String(campaign.id),
            genreIds: [],
          })
          void updateSpotifyCampaignLanguages({
            id: String(campaign.id),
            languageIds: [ANY_LANGUAGE_ID],
          })

          sendCreateFirstCampaignSourceBusterEvent(
            campaign.id,
            CampaignPlatformTypeEnum.SPOTIFY,
            token,
          )

          if (userId) {
            sendFirstCampaignCreatedEvents({
              userId,
              email: user?.user.email || '',
              platform: CampaignPlatformTypeEnum.SPOTIFY,
              campaignId: campaign.id,
              trackId: campaign.track.id,
            })
          }

          navigate(`${createdFirst}/${CampaignPlatformTypeEnum.SPOTIFY}`)
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Spotify campaign creation failed:', error)
        })
    } else if (pending.platform === 'spotify-pre-release') {
      void createPreReleaseSpotifyCampaign(pending.body)
        .unwrap()
        .then((campaign) => {
          void updateSpotifyCampaignGenres({
            id: String(campaign.id),
            genreIds: [],
          })
          void updateSpotifyCampaignLanguages({
            id: String(campaign.id),
            languageIds: [ANY_LANGUAGE_ID],
          })

          sendCreateFirstCampaignSourceBusterEvent(
            campaign.id,
            CampaignPlatformTypeEnum.SPOTIFY,
            token,
          )

          if (userId) {
            sendFirstCampaignCreatedEvents({
              userId,
              email: user?.user.email || '',
              platform: CampaignPlatformTypeEnum.SPOTIFY,
              campaignId: campaign.id,
              trackId: campaign.track.id,
            })
          }

          navigate(
            buildPreReleaseCreatedUrl({
              campaignId: campaign.id,
              status: campaign.status,
            }),
          )
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Pre-release campaign creation failed:', error)
        })
    } else if (pending.platform === 'tiktok') {
      void createNewTiktokCampaign(pending.body)
        .unwrap()
        .then((campaign) => {
          sendCreateFirstCampaignSourceBusterEvent(
            Number(campaign.id),
            CampaignPlatformTypeEnum.TIKTOK,
            token,
          )

          if (userId) {
            sendFirstCampaignCreatedEvents({
              userId,
              email: user?.user.email || '',
              platform: CampaignPlatformTypeEnum.TIKTOK,
              campaignId: campaign.id,
              trackId: campaign.trackId,
            })
          }

          navigate(`${createdFirst}/${CampaignPlatformTypeEnum.TIKTOK}`)
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('TikTok campaign creation failed:', error)
        })
    } else {
      void createAdCampaign(pending.body)
        .unwrap()
        .then((campaign) => {
          sendCreateFirstCampaignSourceBusterEvent(
            campaign.id,
            CampaignPlatformTypeEnum.META,
            token,
          )

          if (userId) {
            artistRegistrationCompleteEvent({
              platformName: 'meta_ads',
              userRole: ArtistRolesEnum.ARTIST,
              userId,
            })
          }

          navigate(`${createdFirst}/${CampaignPlatformTypeEnum.META}`)
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Meta Ads campaign creation failed:', error)
        })
    }
  }, [
    token,
    user,
    navigate,
    createSpotifyCampaign,
    createPreReleaseSpotifyCampaign,
    updateSpotifyCampaignGenres,
    updateSpotifyCampaignLanguages,
    createNewTiktokCampaign,
    createAdCampaign,
    sendCreateFirstCampaignSourceBusterEvent,
  ])

  return null
}
